import { useRef, useState, type ReactNode } from 'react';
import {
  ResultsFilterChips,
  ResultsSortSheet,
  type ResultsFilterChip,
} from '../../shared/results_page/results_filter_chips.js';
import { RentalChipSheets, type RentalChipSheet } from './rental_results_chip_sheets.js';
import {
  RENTAL_ACCESSORIES,
  useRentalFilterCatalog,
  type RentalFilterableOffer,
  type RentalFilterCatalog,
  type RentalResultsFiltersCopy,
  type RentalResultsFilterValue,
} from './rental_results_filters.js';
import { RentalResultsFiltersSheet } from './rental_results_filters_sheet.js';
import {
  DEFAULT_RENTAL_RESULTS_ORDER,
  rentalResultsOrdersOf,
  type RentalResultsOrder,
  type RentalResultsSortCopy,
} from './rental_results_sort.js';

/** A copy que só os chips precisam — o resto vem do painel e do ordenador. */
export type RentalResultsChipsCopy = {
  /** O nome acessível da fila ("Filtros e ordenação"). */
  toolbarLabel: string;
  /** O ← das folhas dos chips ("Voltar"). */
  backLabel: string;
  /** O chip da política, mais curto que o rótulo do painel ("Dentro da
   *  política", como o do hotel). */
  policyLabel: string;
  /** O chip da diária sem corte ("Diária"); o título da folha é o do painel. */
  dailyPriceLabel: string;
  /** O rótulo do chip de diária com corte: só teto, só piso, os dois. */
  priceUpTo: (maximum: number) => string;
  priceFrom: (minimum: number) => string;
  priceBetween: (minimum: number, maximum: number) => string;
  /** As opções marcadas num chip de lista, já em prosa ("Econômico ou SUV"). */
  optionList: (labels: readonly string[]) => string;
};

/** Os cortes em pé — o contador do `Filtros` (o chip e o botão da linha). O
 *  pino do mapa que filtra pela agência (#3995) conta como um corte. */
export function activeRentalFilterCount(
  filters: RentalResultsFilterValue,
  agencyPinned = false,
): number {
  return (
    Number(agencyPinned) +
    Number(filters.policyOnly) +
    Number(filters.categoryIds.length > 0) +
    filters.accessories.length +
    Number(filters.minimumDailyPrice !== undefined || filters.maximumDailyPrice !== undefined) +
    Number(filters.companyIds.length > 0)
  );
}

type OpenSheet = 'filters' | 'sort' | RentalChipSheet | null;

function dailyChipLabel(filters: RentalResultsFilterValue, copy: RentalResultsChipsCopy): string {
  const { minimumDailyPrice: min, maximumDailyPrice: max } = filters;
  if (min !== undefined && max !== undefined) return copy.priceBetween(min, max);
  if (max !== undefined) return copy.priceUpTo(max);
  if (min !== undefined) return copy.priceFrom(min);
  return copy.dailyPriceLabel;
}

/** O rótulo de um chip de lista: o nome do eixo sem corte, as opções marcadas
 *  com corte (na ordem do catálogo, a mesma do painel). */
function listChipLabel(
  options: readonly { id: string; label: string }[],
  selected: readonly string[],
  fallback: string,
  copy: RentalResultsChipsCopy,
): string {
  const labels = options.filter(({ id }) => selected.includes(id)).map(({ label }) => label);
  return labels.length === 0 ? fallback : copy.optionList(labels);
}

/** Os chips de eixo depois da política, na ordem do frame: os acessórios
 *  (liga/desliga) e os de folha (diária, categoria, agência). */
function axisChips({
  filters,
  filtersCopy,
  copy,
  filterCatalog,
  applyFilters,
  openSheet,
}: {
  readonly filters: RentalResultsFilterValue;
  readonly filtersCopy: RentalResultsFiltersCopy;
  readonly copy: RentalResultsChipsCopy;
  readonly filterCatalog: ReturnType<typeof useRentalFilterCatalog>;
  readonly applyFilters: (next: RentalResultsFilterValue) => void;
  readonly openSheet: (next: RentalChipSheet) => () => void;
}): ResultsFilterChip[] {
  return [
    ...RENTAL_ACCESSORIES.map((accessory) => {
      const active = filters.accessories.includes(accessory);
      return {
        id: accessory,
        kind: 'toggle' as const,
        label: filtersCopy.accessoryLabel(accessory),
        active,
        onPress: () => {
          applyFilters({
            ...filters,
            accessories: active
              ? filters.accessories.filter((other) => other !== accessory)
              : [...filters.accessories, accessory],
          });
        },
      };
    }),
    ...(filterCatalog.priceFloor !== undefined && filterCatalog.priceCeiling !== undefined
      ? [
          {
            id: 'daily',
            kind: 'sheet' as const,
            label: dailyChipLabel(filters, copy),
            active:
              filters.minimumDailyPrice !== undefined || filters.maximumDailyPrice !== undefined,
            onPress: openSheet('daily'),
          },
        ]
      : []),
    {
      id: 'category',
      kind: 'sheet' as const,
      label: listChipLabel(
        filterCatalog.categories,
        filters.categoryIds,
        filtersCopy.categoryLabel,
        copy,
      ),
      active: filters.categoryIds.length > 0,
      onPress: openSheet('category'),
    },
    {
      id: 'company',
      kind: 'sheet' as const,
      label: listChipLabel(
        filterCatalog.companies,
        filters.companyIds,
        filtersCopy.companyLabel,
        copy,
      ),
      active: filters.companyIds.length > 0,
      onPress: openSheet('company'),
    },
  ];
}

/**
 * A fila de chips do CARRO no palco estreito — a mesma anatomia da do hotel
 * (#3140, `HotelResultsFilterChips`), na ordem da #5125: `Filtros` com o
 * contador (o painel completo na folha), a ordenação, os liga/desliga que
 * viram na hora (política, ar condicionado, automático) e os chips de folha
 * (diária, categoria, agência). O chip Distância espera o tipo do lugar
 * buscado: só existe em busca por endereço/POI.
 */
export function RentalResultsFilterChips({
  offers,
  catalog,
  currency,
  filters,
  order,
  filtersCopy,
  sortCopy,
  copy,
  applyLabel,
  locked,
  countsPartial,
  agencyPinned,
  onAgencyUnpin,
  onQueryChange,
}: {
  readonly offers: readonly RentalFilterableOffer[];
  readonly catalog: RentalFilterCatalog | undefined;
  readonly currency: string;
  readonly filters: RentalResultsFilterValue;
  readonly order: RentalResultsOrder;
  readonly filtersCopy: RentalResultsFiltersCopy;
  readonly sortCopy: RentalResultsSortCopy;
  readonly copy: RentalResultsChipsCopy;
  /** O botão do pé da folha de filtros ("Aplicar filtros"). */
  readonly applyLabel: string;
  readonly locked: boolean;
  readonly countsPartial: boolean;
  /** O pino do mapa filtra a lista: conta no `Filtros` e sai no `Redefinir`. */
  readonly agencyPinned: boolean;
  readonly onAgencyUnpin: () => void;
  readonly onQueryChange: (query: {
    filters: RentalResultsFilterValue;
    order: RentalResultsOrder;
  }) => void;
}): ReactNode {
  const [sheet, setSheet] = useState<OpenSheet>(null);
  const filterCatalog = useRentalFilterCatalog(offers, catalog);
  const chipRefs = useRef(new Map<string, HTMLButtonElement>());
  /** O chip que abriu a folha em cena — o alvo do foco de volta. */
  const openerRef = useRef<HTMLElement | null>(null);
  const applyFilters = (next: RentalResultsFilterValue) => {
    onQueryChange({ filters: next, order });
  };
  const openSheet = (next: Exclude<OpenSheet, null>) => () => {
    openerRef.current = chipRefs.current.get(next) ?? null;
    setSheet(next);
  };
  const onOpenChange = (open: boolean) => {
    if (!open) setSheet(null);
  };
  const count = activeRentalFilterCount(filters, agencyPinned);
  const chips: ResultsFilterChip[] = [
    {
      id: 'filters',
      kind: 'filters',
      label: filtersCopy.filtersLabel,
      active: count > 0,
      badge: count,
      onPress: openSheet('filters'),
    },
    {
      id: 'sort',
      kind: 'sort',
      label: sortCopy.orderLabel(order),
      active: order !== DEFAULT_RENTAL_RESULTS_ORDER,
      onPress: openSheet('sort'),
    },
    {
      id: 'policy',
      kind: 'toggle',
      label: copy.policyLabel,
      active: filters.policyOnly,
      onPress: () => {
        applyFilters({ ...filters, policyOnly: !filters.policyOnly });
      },
    },
    ...axisChips({ filters, filtersCopy, copy, filterCatalog, applyFilters, openSheet }),
  ];
  return (
    <>
      <ResultsFilterChips
        chips={chips}
        label={copy.toolbarLabel}
        disabled={locked}
        chipRefs={chipRefs}
      />
      <RentalResultsFiltersSheet
        open={sheet === 'filters'}
        onOpenChange={onOpenChange}
        returnFocusTo={openerRef}
        offers={offers}
        catalog={catalog}
        currency={currency}
        value={filters}
        copy={filtersCopy}
        applyLabel={applyLabel}
        locked={locked}
        countsPartial={countsPartial}
        agencyPinned={agencyPinned}
        onAgencyUnpin={onAgencyUnpin}
        onApply={applyFilters}
      />
      <RentalChipSheets
        sheet={sheet === 'filters' || sheet === 'sort' ? null : sheet}
        onOpenChange={onOpenChange}
        returnFocusTo={openerRef}
        offers={offers}
        currency={currency}
        filters={filters}
        copy={filtersCopy}
        backLabel={copy.backLabel}
        applyLabel={applyLabel}
        locked={locked}
        countsPartial={countsPartial}
        catalog={filterCatalog}
        onApply={applyFilters}
      />
      <ResultsSortSheet
        open={sheet === 'sort'}
        onOpenChange={onOpenChange}
        returnFocusTo={openerRef}
        title={sortCopy.sortLabel}
        backLabel={copy.backLabel}
        orders={rentalResultsOrdersOf(offers)}
        value={order}
        orderLabel={(candidate) => sortCopy.orderLabel(candidate)}
        locked={locked}
        onChange={(next) => {
          onQueryChange({ filters, order: next });
        }}
      />
    </>
  );
}
